import { create } from 'zustand';
import { expandTileSelection } from '@/utils/variantA.js';

/**
 * Zustand store for occupied tile data.
 *
 * Stores sponsors with compressed Variant-A format (bundles, ranges).
 * Provides selectors that efficiently check if a tile is occupied.
 * Includes an expandedCache for O(1) lookups during rendering.
 *
 * Isolated from context so background API polls (every 5 s) don't trigger a
 * context update and re-render the entire tree.
 */
export const useOccupiedStore = create( ( set, get ) => ( {
	/**
	 * Sponsors data by field type, with tile_ids in compressed Variant-A format.
	 * @type {Record<string, object[]>}
	 */
	sponsors: {},

	/**
	 * Expanded cache for fast lookups: fieldType → tileId → occupied entry.
	 * Regenerated when sponsors change.
	 * @type {Record<string, Record<string, object>>}
	 */
	expandedCache: {},

	/**
	 * Replace occupied sponsors for one field type (with compressed tile_ids).
	 * Regenerates expandedCache for fast rendering.
	 *
	 * @param {string}   fieldType
	 * @param {object[]} entries - Sponsor entries with tile_ids in Variant-A format
	 * @param {Array}    normalizedBundles - Bundle definitions for expansion
	 */
	setOccupied( fieldType, entries, normalizedBundles = [] ) {
		set( ( state ) => {
			// Quick reference check: if array references are identical, nothing changed
			const prevEntries = state.sponsors[ fieldType ] ?? [];
			if ( prevEntries === entries || ( Array.isArray( entries ) && Array.isArray( prevEntries ) && prevEntries.length === entries.length && prevEntries.every( ( e, i ) => entries[ i ] === e ) ) ) {
				return state; // No change, skip update
			}

			// Build expanded cache by expanding all tile_ids
			const newExpandedCache = {};
			for ( const entry of entries ) {
				const expandedIds = entry.tile_ids ? expandTileSelection( entry.tile_ids, normalizedBundles ) : [];
				for ( const tileId of expandedIds ) {
					newExpandedCache[ tileId ] = entry;
				}
			}

			return {
				sponsors: { ...state.sponsors, [ fieldType ]: entries },
				expandedCache: { ...state.expandedCache, [ fieldType ]: newExpandedCache },
			};
		} );
	},

	/**
	 * Check if a specific tile is occupied (using expanded cache).
	 * @param {string} fieldType
	 * @param {string} tileId
	 * @returns {object|null} Occupied entry or null
	 */
	getOccupiedTile( fieldType, tileId ) {
		const cache = get().expandedCache[ fieldType ] ?? {};
		return cache[ tileId ] ?? null;
	},
} ) );
